import { Injectable } from '@angular/core';
import {
    MediaDocumentType,
    base64ToDataUrl,
    dataURItoBlob,
    fileTypeFromFileName
} from '@caballus/common';
import { CurrentRide, Ride, User, WayPoint } from '@caballus/ui-common';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { CapgoCapacitorDataStorageSqlite } from '@capgo/capacitor-data-storage-sqlite';
import { environment } from '@ion-caballus/env';
import write_blob from 'capacitor-blob-writer';
import { BehaviorSubject, timer } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
    StoragePerformanceMonitor,
    UniqueOperationStats,
    measureStorageOperation
} from './storage-performance.decorator';

export interface WayPointBuffer {
    key: string;
    waypoint: WayPoint;
}

interface CacheEntry<T> {
    value: T;
    timestamp: number;
}

class MemoryCache {
    private static instance: MemoryCache;
    private cache = new BehaviorSubject<Map<string, CacheEntry<any>>>(new Map());
    private CACHE_DURATION = 5000; // 5 seconds in milliseconds

    private constructor() {}

    static getInstance(): MemoryCache {
        if (!MemoryCache.instance) {
            MemoryCache.instance = new MemoryCache();
        }
        return MemoryCache.instance;
    }

    async get<T>(key: string): Promise<T | null> {
        const entry = this.cache.value.get(key);
        if (entry && Date.now() - entry.timestamp < this.CACHE_DURATION) {
            return entry.value;
        }
        if (entry) {
            // Remove expired entry
            this.cache.value.delete(key);
            this.cache.next(this.cache.value);
        }
        return null;
    }

    set<T>(key: string, value: T): void {
        const newMap = new Map(this.cache.value);
        newMap.set(key, {
            value,
            timestamp: Date.now()
        });
        this.cache.next(newMap);
    }
}

@Injectable({
    providedIn: 'root'
})
export class StorageService {
    private _store: typeof CapgoCapacitorDataStorageSqlite;
    private _isService: boolean;
    private _waypointBuffer: WayPointBuffer[] = [];
    private _MAX_BUFFER_COUNT = 10;

    // applicable to all users
    private readonly _commonPrefix: string = 'common-data-';
    private readonly _keyUser: string = this._commonPrefix + 'user';
    private readonly _keyAuthToken: string = this._commonPrefix + 'auth-token';
    private readonly _keyRefreshToken: string = this._commonPrefix + 'refresh-token';
    // applicable to current logged in user only
    private readonly _userPrefix: string = 'user-data-';
    private readonly _currentRidePrefix: string = this._userPrefix + 'current-ride-';

    private readonly _unsyncedRidesPrefix = 'unsynced-rides-';
    private readonly _unsyncedWaypointsPrefix = 'unsynced-waypoints-';
    private readonly _waypointChunksPrefix = 'waypoint-chunks-';

    /**
     * Initializes the backing storage to this service. MUST be called in
     * APP_INITIALIZER. {@see AppModule}
     */
    public async init(): Promise<void> {
        this._store = CapgoCapacitorDataStorageSqlite;
        this._isService = true;

        if (!environment.production) {
            timer(0, 30000)
                .pipe(
                    tap(() => {
                        console.log('StorageService: Metrics', this.getStorageMetrics());
                        console.log(
                            'StorageService: Average operation time',
                            this.getAverageOperationTime('get')
                        );
                        console.log(
                            'StorageService: Average operation time',
                            this.getAverageOperationTime('set')
                        );
                        console.log(
                            'StorageService: Unique operation stats',
                            this.getUniqueOperationStats()
                        );
                        console.log('--------------------------------');
                        this.clearStorageMetrics();
                    })
                )
                .subscribe();
        }

        await this.openStore();
    }

    /**
     * Open a Store
     * @param _dbName string optional
     * @param _table string optional
     * @param _encrypted boolean optional
     * @param _mode string optional
     */
    async openStore(
        _dbName?: string,
        _table?: string,
        _encrypted?: boolean,
        _mode?: string
    ): Promise<void> {
        if (this._isService && this._store != null) {
            const database: string = _dbName ? _dbName : 'storage';
            const table: string = _table ? _table : 'storage_table';
            const encrypted: boolean = _encrypted ? _encrypted : false;
            const mode: string = _mode ? _mode : 'no-encryption';
            try {
                await this._store.openStore({ database, table, encrypted, mode });
                return Promise.resolve();
            } catch (err) {
                return Promise.reject(err);
            }
        } else {
            return Promise.reject(new Error('openStore: Store not opened'));
        }
    }

    private async _getUserKey(baseKey: string): Promise<string> {
        const user = await this.getUser();
        return user ? `${baseKey}${user._id}` : '';
    }

    @measureStorageOperation()
    public async setUser(user: User): Promise<void> {
        const data = JSON.stringify(user);
        await this._store.set({ key: this._keyUser, value: data });
    }

    @measureStorageOperation()
    public async getUser(): Promise<User> {
        return this.getWithCache<User>(this._keyUser, async () => {
            const json = await this._store.get({ key: this._keyUser });
            return json.value ? new User(JSON.parse(json.value)) : null;
        });
    }

    @measureStorageOperation()
    public async clearUser(): Promise<void> {
        await this._store.remove({ key: this._keyUser });
    }

    @measureStorageOperation()
    public async setAuthToken(value: string): Promise<void> {
        await this._store.set({ key: this._keyAuthToken, value });
    }

    @measureStorageOperation()
    public async getAuthToken(): Promise<string> {
        const token = await this._store.get({ key: this._keyAuthToken });
        return !!token.value && typeof token.value === 'string' ? token.value : null;
    }

    @measureStorageOperation()
    public async clearAuthToken(): Promise<void> {
        await this._store.remove({ key: this._keyAuthToken });
    }

    @measureStorageOperation()
    public async setRefreshToken(refreshToken: string): Promise<void> {
        await this._store.set({ key: this._keyRefreshToken, value: refreshToken });
    }

    @measureStorageOperation()
    public async getRefreshToken(): Promise<string> {
        const token = await this._store.get({ key: this._keyRefreshToken });
        return !!token.value && typeof token.value === 'string' ? token.value : null;
    }

    @measureStorageOperation()
    public async clearRefreshToken(): Promise<void> {
        await this._store.remove({ key: this._keyRefreshToken });
    }

    @measureStorageOperation()
    public async getUserData<T = string>(subKey: string, useCache: boolean = true): Promise<T> {
        const key = await this._getUserKey(`${this._userPrefix}${subKey}`);
        return useCache
            ? this.getWithCache<T>(key, async () => {
                  const data = await this._store.get({ key });
                  return data.value ? (JSON.parse(data.value) as T) : null;
              })
            : (async () => {
                  const data = await this._store.get({ key });
                  return data.value ? (JSON.parse(data.value) as T) : null;
              })();
    }

    @measureStorageOperation()
    public async setUserData<T = string>(subKey: string, data: T): Promise<void> {
        const key = await this._getUserKey(`${this._userPrefix}${subKey}`);
        if (key == null) {
            return null;
        }
        const value = typeof data !== 'string' ? JSON.stringify(data) : (data as string);
        await this._store.set({
            key,
            value
        });
    }

    public async storeBlob(
        blob: Blob,
        fileName: string,
        mediaType: MediaDocumentType,
        thumbnail: boolean
    ): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            // Secondly, write the Blob to disk. The 'write_blob' function takes an options
            // object and returns a Promise, which resolves once the file has been
            // successfully written.

            const path =
                mediaType === MediaDocumentType.Document
                    ? 'documents'
                    : thumbnail
                      ? 'thumbnails'
                      : 'images';
            write_blob({
                // The 'path' option should be a string describing where to write the file. It
                // may be specified as an absolute URL (beginning with "file://") or a relative
                // path, in which case it is assumed to be relative to the 'directory' option.

                path: `${path}/${fileName}`,

                // The 'directory' option is used to resolve 'path' to a location on the disk.
                // It is ignored if the 'path' option begins with "file://".

                directory: Directory.Data,

                // The 'blob' option must be a Blob, which will be written to the file. The file
                // on disk is overwritten, not appended to.

                blob,

                // Fast mode vastly improves read and write speeds on the web platform. For
                // files written with 'fast_mode' set to true, Filesystem.readFile will produce
                // a Blob rather than a Base64-encoded string. The 'fast_mode' option is
                // ignored on iOS and Android. For backwards compatibility, it defaults to
                // false.

                fast_mode: true,

                // If the 'recursive' option is 'true', intermediate directories will be created
                // as required. It defaults to 'false' if not specified.

                recursive: true,

                // If 'write_blob' falls back to its alternative strategy on failure, the
                // 'on_fallback' function will be called with the underlying error. This can be
                // useful to diagnose slow writes. It is optional.

                // See the "Fallback mode" section below for a detailed explanation.

                on_fallback(error) {
                    reject(error);
                }
            })
                .then(() => {
                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    public async getBlob(
        fileName: string,
        mediaType: MediaDocumentType,
        thumbnail: boolean
    ): Promise<Blob> {
        return new Promise<Blob>((resolve, reject) => {
            const path =
                mediaType === MediaDocumentType.Document
                    ? 'documents'
                    : thumbnail
                      ? 'thumbnails'
                      : 'images';
            Filesystem.readFile({
                path: `${path}/${fileName}`,
                directory: Directory.Data
            })
                .then(function ({ data }) {
                    if (typeof data === 'string') {
                        const fileType = thumbnail ? 'image/png' : fileTypeFromFileName(fileName);
                        const blob = dataURItoBlob(base64ToDataUrl(data, fileType));
                        resolve(blob);
                    } else {
                        resolve(data);
                    }
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    }

    public removeBlob(fileName: string, thumbnail: boolean): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            Filesystem.deleteFile({
                path: `${thumbnail ? 'thumbnails' : 'images'}/${fileName}`,
                directory: Directory.Data
            })
                .then(() => {
                    resolve();
                })
                .catch(err => {
                    reject(err);
                });
        });
    }

    @measureStorageOperation()
    public async setCurrentRide(ride: CurrentRide): Promise<void> {
        if (!ride) {
            await this.clearUserData(this._currentRidePrefix);
            return;
        }
        await this.setUserData(this._currentRidePrefix, ride);
    }

    @measureStorageOperation()
    public async getCurrentRide(useCache: boolean = false): Promise<CurrentRide> {
        const ride = await this.getUserData<CurrentRide>(this._currentRidePrefix, useCache);
        if (!ride) {
            return null;
        }

        const rideObj = new Ride(ride);
        
        // Merge waypoint chunks if they exist
        if (rideObj._id) {
            const waypointChunks = await this.getWaypointChunks(rideObj._id);
            if (waypointChunks.length > 0) {
                // Merge chunks into the ride's paths
                if (!rideObj.paths?.length) {
                    rideObj.paths = [
                        {
                            startDateTime: rideObj.startDateTime,
                            endDateTime: undefined,
                            wayPoints: []
                        }
                    ];
                }
                rideObj.paths[0].wayPoints = waypointChunks;
            }
        }

        return rideObj;
    }

    @measureStorageOperation()
    public async clearCurrentRideData(): Promise<void> {
        // Get the ride first to get its ID for chunk cleanup
        const ride = await this.getUserData<CurrentRide>(this._currentRidePrefix, false);
        if (ride?._id) {
            await this.clearWaypointChunks(ride._id);
        }
        await this.clearUserData(this._currentRidePrefix);
    }

    @measureStorageOperation()
    public async clearUserData(subKey: string): Promise<void> {
        const key = await this._getUserKey(`${this._userPrefix}${subKey}`);
        await this._store.remove({ key });
    }

    @measureStorageOperation()
    public async setTour(): Promise<void> {
        await this._store.set({ key: 'tour', value: 'false' });
    }

    @measureStorageOperation()
    public async getTour(): Promise<boolean> {
        return (await this._store.get({ key: 'tour' })).value === 'true';
    }

    @measureStorageOperation()
    public async storeUnsyncedRide(ride: Ride): Promise<void> {
        const existingRides = await this.getUnsyncedRides();
        const updatedRides = [...(existingRides || []), ride];
        await this.setUserData(this._unsyncedRidesPrefix, updatedRides);
    }

    @measureStorageOperation()
    public async getUnsyncedRides(): Promise<Ride[]> {
        const rides = await this.getUserData<Ride[]>(this._unsyncedRidesPrefix, false);
        if (!Array.isArray(rides)) {
            return [];
        }

        // Merge waypoint chunks for each ride
        const ridesWithWaypoints = await Promise.all(
            rides.map(async r => {
                const ride = new Ride(r);
                if (ride._id) {
                    const waypointChunks = await this.getWaypointChunks(ride._id);
                    if (waypointChunks.length > 0) {
                        // Merge chunks into the ride's paths
                        if (!ride.paths?.length) {
                            ride.paths = [
                                {
                                    startDateTime: ride.startDateTime,
                                    endDateTime: undefined,
                                    wayPoints: []
                                }
                            ];
                        }
                        ride.paths[0].wayPoints = waypointChunks;
                    }
                }
                return ride;
            })
        );

        return ridesWithWaypoints;
    }

    @measureStorageOperation()
    public async removeUnsyncedRide(rideId: string): Promise<void> {
        const existingRides = await this.getUnsyncedRides();
        const updatedRides = existingRides.filter(ride => ride._id !== rideId);
        await this.setUserData(this._unsyncedRidesPrefix, updatedRides);

        // Also clean up associated waypoints
        await this.clearUnsyncedWaypoints(rideId);
        // Clean up waypoint chunks
        await this.clearWaypointChunks(rideId);
    }

    @measureStorageOperation()
    public async storeUnsyncedWaypoints(rideId: string, waypoints: WayPoint[]): Promise<void> {
        const key = `${this._unsyncedWaypointsPrefix}${rideId}`;
        await this.setUserData(key, waypoints);
    }

    @measureStorageOperation()
    public async clearUnsyncedWaypoints(rideId: string): Promise<void> {
        const key = `${this._unsyncedWaypointsPrefix}${rideId}`;
        await this.clearUserData(key);
    }

    @measureStorageOperation()
    public async clearAllUnsyncedData(): Promise<void> {
        const rides = await this.getUnsyncedRides();
        for (const ride of rides) {
            await this.clearUnsyncedWaypoints(ride._id);
            await this.clearWaypointChunks(ride._id);
        }
        await this.clearUserData(this._unsyncedRidesPrefix);
    }

    @measureStorageOperation()
    public async storeWaypointChunk(
        rideId: string,
        chunkIndex: number,
        waypoints: WayPoint[]
    ): Promise<void> {
        const key = `${this._waypointChunksPrefix}${rideId}-chunk-${chunkIndex}`;
        console.log('Storing waypoint chunk', key, waypoints.length);
        await this.setUserData(key, waypoints);
    }

    @measureStorageOperation()
    public async getWaypointChunkCount(rideId: string): Promise<number> {
        // We need to get all keys and count chunks for this ride
        // Since we don't have a listKeys method, we'll use a counter stored separately
        const counterKey = `${this._waypointChunksPrefix}${rideId}-count`;
        const count = await this.getUserData<number>(counterKey, false);
        return count || 0;
    }

    @measureStorageOperation()
    public async incrementWaypointChunkCount(rideId: string): Promise<number> {
        const counterKey = `${this._waypointChunksPrefix}${rideId}-count`;
        const currentCount = await this.getWaypointChunkCount(rideId);
        const newCount = currentCount + 1;
        await this.setUserData(counterKey, newCount);
        return newCount;
    }

    @measureStorageOperation()
    public async getWaypointChunks(rideId: string): Promise<WayPoint[]> {
        const chunkCount = await this.getWaypointChunkCount(rideId);
        const allWaypoints: WayPoint[] = [];

        for (let i = 0; i < chunkCount; i++) {
            const chunkKey = `${this._waypointChunksPrefix}${rideId}-chunk-${i}`;
            const chunk = await this.getUserData<WayPoint[]>(chunkKey, false);
            if (chunk && Array.isArray(chunk)) {
                allWaypoints.push(...chunk.map(wp => new WayPoint(wp)));
            }
        }

        return allWaypoints;
    }

    @measureStorageOperation()
    public async clearWaypointChunks(rideId: string): Promise<void> {
        const chunkCount = await this.getWaypointChunkCount(rideId);

        // Clear all chunks
        for (let i = 0; i < chunkCount; i++) {
            const chunkKey = `${this._waypointChunksPrefix}${rideId}-chunk-${i}`;
            await this.clearUserData(chunkKey);
        }

        // Clear the counter
        const counterKey = `${this._waypointChunksPrefix}${rideId}-count`;
        await this.clearUserData(counterKey);
    }

    public getStorageMetrics() {
        return StoragePerformanceMonitor.getMetrics();
    }

    public getAverageOperationTime(operation: string): number {
        return StoragePerformanceMonitor.getAverageTime(operation);
    }

    public clearStorageMetrics(): void {
        StoragePerformanceMonitor.clearMetrics();
    }

    public getUniqueOperationStats(): Record<string, UniqueOperationStats> {
        return StoragePerformanceMonitor.getOperationStats();
    }

    private async getWithCache<T>(key: string, getter: () => Promise<T>): Promise<T> {
        const cache = MemoryCache.getInstance();
        const cachedValue = await cache.get<T>(key);

        if (cachedValue !== null) {
            // Create a new object with the cached value and fromCache property
            return {
                ...(typeof cachedValue === 'object' ? cachedValue : { value: cachedValue }),
                fromCache: true
            } as T;
        }

        const value = await getter();
        cache.set(key, value);
        // Create a new object with the value and fromCache property
        return {
            ...(typeof value === 'object' ? value : { value }),
            fromCache: false
        } as T;
    }
}
